import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, LogOut, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { authService } from '@/services/auth';
import { session } from '@/lib/session';
import {
  isOnCanonicalLoginOrigin,
  redirectToPlatformLogin,
} from '@/lib/platform-login';
import {
  TRIAL_END_LABEL,
  TRIAL_RENEWAL_LABEL,
  TRIAL_TENANT_NAME,
} from './trial';

const SALES_EMAIL = 'sales@zafroi.com';

/**
 * Trial-ended hard gate. Replaces the entire PWA outlet once the
 * trial cut-off has passed. No bypass UI; the only exits are
 *
 *   - tapping the subscription contact CTAs (email)
 *   - signing out
 *
 * The gate is mounted at the layout level so it fires regardless of
 * which deep route the operator landed on (bookmark, installed home
 * icon, etc.) — they can't slip past it by hitting /pwa/pack/<id>
 * directly.
 */
export function PwaTrialExpired() {
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  const onSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await authService.logout();
    } catch {
      /* swallow */
    }
    session.clear();
    toast.success('Signed out');
    if (!isOnCanonicalLoginOrigin()) {
      redirectToPlatformLogin();
      return;
    }
    navigate('/login', { replace: true });
  };

  return (
    <div className="pwa-anim-in flex flex-col gap-5 px-4 pb-10 pt-6">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="grid h-16 w-16 place-items-center rounded-2xl bg-primary/15 text-primary">
          <Lock className="h-7 w-7" />
        </div>
        <h1 className="text-[24px] font-bold leading-tight tracking-tight text-foreground">
          Trial ended
        </h1>
        <p className="max-w-xs text-[13px] leading-snug text-muted-foreground">
          The {TRIAL_TENANT_NAME} beta of Zafroi Ops was valid until{' '}
          <strong className="text-foreground">{TRIAL_END_LABEL}</strong>. An
          active subscription is required from{' '}
          <strong className="text-foreground">{TRIAL_RENEWAL_LABEL}</strong>{' '}
          onwards to continue.
        </p>
      </div>

      <div className="space-y-2 rounded-2xl border border-border/70 bg-card p-4">
        <div className="flex items-center gap-2 text-[12px] font-medium text-foreground">
          <ShieldCheck className="h-4 w-4 text-primary" />
          To continue using Zafroi Ops
        </div>
        <p className="text-[12px] leading-snug text-muted-foreground">
          Get in touch with the Zafroi team to activate your subscription —
          your data, settings, and security seals are preserved and pick up
          exactly where you left off once activation lands.
        </p>
        <div className="pt-2">
          <a
            href={`mailto:${SALES_EMAIL}?subject=${encodeURIComponent(
              `${TRIAL_TENANT_NAME} — Zafroi Ops subscription`,
            )}&body=${encodeURIComponent(
              `Hi Zafroi team,\n\nWe'd like to continue using Zafroi Ops after the ${TRIAL_TENANT_NAME} beta. Please share the subscription terms.\n\nThanks,`,
            )}`}
            className="pwa-tap inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 text-[14px] font-semibold text-primary-foreground shadow-sm active:opacity-90"
          >
            <Mail className="h-4 w-4" />
            Email {SALES_EMAIL}
          </a>
        </div>
      </div>

      <button
        type="button"
        onClick={onSignOut}
        disabled={signingOut}
        className="pwa-tap inline-flex h-11 items-center justify-center gap-2 rounded-2xl text-[12.5px] font-medium text-destructive active:bg-destructive/10 disabled:opacity-50"
      >
        <LogOut className="h-4 w-4" />
        Sign out
      </button>
    </div>
  );
}
